/**
 * learningface.ai v1.0.0
 *
 * Copyright (c) INFERable, a Public Benefit Corporation.
 *
 * All rights reserved.
 *
 */
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import ReactDOM from 'react-dom'; // Import ReactDOM

import Logo from './INFERableLearningFaceLogo.png';
import Tiles from './tiles'; // Adjust the path if `tiles.jsx` is in a different folder

import {CrudUI} from './crud-ui'; // Adjust the path if `crud-ui.jsx` is in a different folder
import {enableAuthButtons, checkAuthStatus, getAuthData} from './auth.js';
import {
    fetchObjects, fetchObject
} from './api-interactions.js';

function LegalOverlay() {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (!localStorage.getItem('legalAccepted')) {
            setVisible(true);
        }
    }, []);

    // Accept button: logs acceptance and hides overlay permanently
    const accept = () => {
        localStorage.setItem('legalAccepted', 'true');
        setVisible(false);
    };

    // Close button: hides overlay for this session only
    const close = () => {
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            <div style={{
                background: '#fff',
                borderRadius: '8px',
                maxWidth: '500px',
                width: '90vw',
                maxHeight: '90vh',
                padding: '2rem',
                position: 'relative',
                boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <button
                    onClick={close}
                    aria-label="Close"
                    style={{
                        position: 'absolute',
                        top: '12px',
                        right: '16px',
                        background: 'none',
                        border: 'none',
                        fontSize: '1.5rem',
                        color: '#333',
                        cursor: 'pointer'
                    }}
                >&times;</button>
                <h5 style={{marginTop: 0}}>Notice & Terms of Use</h5>
                <p style={{fontSize: '1rem'}}>
                    This site is provided by INFERable, a Public Benefit Corporation, and its affiliates, on an "<b>as is</b>" basis and <b>without warranties of any kind, either express or implied</b>.<br />
                    <br /><b>Your use of this site signifies your agreement to these terms.</b><br />
<br/>By continuing to use this site, you acknowledge and accept that:<br />
&bull; <b>Content and Information Collection:</b> Your activity and certain personal information, such as email addresses for authenticated users and contributors, may be collected. Please review our [Link to Privacy Policy] for more details on how we collect, use, and protect your information.<br />
&bull; <b>User Responsibility for Content:</b> You are solely responsible for any content you submit to this site. You agree that your content will not violate any applicable laws, including but not limited to copyright, trademark, privacy, or defamation laws. We reserve the right, but have no obligation, to monitor, edit, or remove any content at our sole discretion.<br />
&bull; <b>Indemnification:</b> You agree to indemnify, defend, and hold harmless INFERable, its affiliates, officers, directors, employees, and agents from and against any and all claims, liabilities, damages, losses, costs, and expenses (including reasonable attorneys' fees) arising out of or in any way connected with your access to or use of the site, your submission of content, or your violation of these terms.<br />
&bull; <b>Cookies:</b> Cookies and similar technologies may be used to support required site functions and enhance your user experience. For more information, please see our [Link to Cookie Policy].<br />
&bull; <b>No Legal Advice:</b> The content on this site, including user-contributed content, is for informational purposes only and does not constitute legal, financial, medical, or professional advice. You should consult with a qualified professional for advice tailored to your specific situation.<br />
 By proceeding, you confirm that you have read, understood, and agree to be bound by these terms. If you do not agree to these terms, please discontinue your use of the site.<br />
                </p>
                <button
                    className="btn btn-primary"
                    onClick={accept}
                    style={{marginTop: '1rem', width: '100%'}}
                >Accept</button>
            </div>
        </div>
    );
}

function MenuWidget() {
    const [isMenuVisible, setMenuVisible] = useState(false);

    const toggleMenu = () => {
        setMenuVisible(!isMenuVisible);
    };

    const hideMenu = () => {
        setMenuVisible(false); // Hide the menu
    };

    useEffect(() => {
        enableAuthButtons(); // Add event listeners for buttons
        checkAuthStatus(); // Check status on page load
        const menuItems = document.querySelectorAll('.menu-options li a');
        menuItems.forEach((item) => {
            item.addEventListener('click', hideMenu);
        });

        // Cleanup event listeners on unmount
        return () => {
            menuItems.forEach((item) => {
                item.removeEventListener('click', hideMenu);
            });
        };
    }, []);

    return (
        <>
            <div className="container mt-4 masthead">
                <div className="masthead-left" id="masthead-home-link">
                    <img
                        src="https://jgoodell2.github.io/learningface/learningface.ai/INFERableLearningFaceLogo.png"
                        style={{ height: '60px' }}
                        alt="INFERable LearningFace Logo"
                    />
                    <div className="learningface-container">
                        <p className="learningface">
                            Learning Face<span className="learningface-tag">&trade; (alpha)</span>
                        </p>
                        <p className="learningface-tag"> democratizing the power of learning analytics</p>
                        <p className="learningface-tag">
                            a service of <strong><a href="https://inferable.app">INFERable</a></strong>&trade;
                        </p>
                    </div>
                </div>
                <div id="authStatus" className="mb-6 auth-text">Loading...</div>
                <div>
                    <a id="loginButton" href="#" className="">
                        Login with Google
                    </a>
                    <a id="logoutButton" href="#" className="hidden mt-4">
                        Logout
                    </a>
                </div>
                <div className="menu-widget">
                    <div className="menu-icon" onClick={toggleMenu}>
                        <i className="fas fa-bars"></i>
                    </div>
                    <ul className={`menu-options ${isMenuVisible ? 'visible' : ''}`}>
                        <li><Link to="/user-details" data-page="user-details">User Profile</Link></li>
                        <li><Link to="/" data-page="home">Home</Link></li>
                        <li><Link to="/crud" data-page="publish">Publish my Model</Link></li>
                        <li><Link to="/about-lf" data-page="about-lf">About LearningFace</Link></li>
                        <li><Link to="/about-inf" data-page="about-inf">About INFERable</Link></li>
                        <li><Link to="/use" data-page="use-models">Learning Analytics as a Service (LAaaS&trade;)</Link></li>
                        <li><Link to="/join" data-page="join">Join the Team</Link></li>
                        <li><Link to="/site-terms" data-page="site-terms">Terms and Conditions</Link></li>
                    </ul>
                </div>
            </div>
        </>
    );
}

function HomePage() {
    const [models, setModels] = useState([]); // State to store the models
    const colorPalettes = ['bg-palette-1', 'bg-palette-2', 'bg-palette-3', 'bg-palette-4', 'bg-palette-5'];

    // Fetch models and update state
    useEffect(() => {
        const fetchModels = async () => {
            try {
                const objects = await fetchObjects();
                setModels(objects);
            } catch (error) {
                console.error('Error fetching models:', error);
            }
        };

        fetchModels();
    }, []);

    return (
        <div className="container mt-4">
            <h2 className="text-center mb-4">Learning Analytics Models</h2>
            <div id="model-grid" className="row row-cols-1 row-cols-md-2 row-cols-lg-3 gy-4">
                {models.map((model, index) => (
                    <div key={index} className="col-md-6 col-lg-4 mb-4">
                        <div className={`model-tile h-100 ${colorPalettes[index % colorPalettes.length]}`} data-model-index={index}>
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <h5 className="tile-title">{model.name}</h5>
                                    <p className="tile-category">{model.category}</p>
                                </div>
                            </div>
                            <p className={`tile-description ${colorPalettes[index % colorPalettes.length]}`}>{model.description}</p>
                            <div className="mt-auto d-flex justify-content-around align-items-center">
                                <button className="btn btn-sm btn-outline-primary info-button" data-model-index={index}>
                                    <Link className="info-button-link" to={`/model-details/${model.id}`} data-page="model-details">Info</Link>
                                </button>
                                <button className="btn btn-sm btn-outline-primary use-button" data-model-index={index}>
                                    <Link className="use-button-link" to="/use" data-page="use-models-page">Use</Link>
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

import { useParams } from 'react-router-dom'; // Import useParams to access URL parameters
function ModelDetails() {
    const { id } = useParams(); // Extract the `id` parameter from the URL
    const [model, setModel] = useState(null); // State to store the model
    const [authData, setAuthData] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    // Fetch the model data when the component mounts
    useEffect(() => {
           const fetchAll = async () => {
            try {
                const [auth, object] = await Promise.all([
                    getAuthData(),
                    fetchObject(id)
                ]);
                setAuthData(auth);
                setModel(object);
            } catch (error) {
                console.error('Error fetching model or auth:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, [id]);

    if (loading) {
        return (
            <div className="container mt-5">
                <h2>Loading...</h2>
            </div>
        );
    }

    if (!authData || !authData.isAuthenticated) {
        return (
            <div className="container mt-5">
                <h1>Authentication Required</h1>
                <div id="auth-section">
                    <p>Please log in to see model details.</p>
                </div>
            </div>
        );
    }

    if (!model) {
        return (
            <div className="container mt-5">
                <h2>Model not found.</h2>
            </div>
        );
    }


    // Render the model details
    return (
        <div className="container mt-5">
            <div id="model-details" className="mt-6 text-gray-700">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <button
                        id="home-button"
                        className="btn btn-secondary"
                        onClick={() => navigate('/')}
                    >
                        Home
                    </button>
                </div>
                <div className={`model-tile h-100 bg-palette-1`}>
                    <div className="d-flex justify-content-between align-items-start">
                        <div>
                            <h5 className="tile-title">{model.name}</h5>
                            <p className="tile-category">{model.category}</p>
                            <p className="tile-description">{model.description}</p>
                            <p className="tile-model-type"></p>
                            <p className="tile-more">-- Additional model details here. --</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function AboutLearningFace() {
    const navigate = useNavigate();
    return (
        <div class="container">
         <div id="about-page" className="mt-6 text-gray-700 hidden">
         <div className="d-flex justify-content-between align-items-center mb-3">
                    <button
                        id="home-button"
                        className="btn btn-secondary"
                        onClick={() => navigate('/')}
                    >
                        Home
                    </button>
                </div>
            <h2 className="text-center mb-4">About Learning Face</h2>
            <p>LerningFace.ai is a service of INFERable that bridges learning analytics researchers and practitioners. 
                It is a forum for researchers to publish their models and it lowers the bar for practitioners to take 
                advantage of research-based learning analytics models.</p>
        </div>
        </div>
    );
}

function AboutInferable() {
    const navigate = useNavigate();
    return (
        <div class="container">
         <div id="about-page" className="mt-6 text-gray-700 hidden">
         <div className="d-flex justify-content-between align-items-center mb-3">
                    <button
                        id="home-button"
                        className="btn btn-secondary"
                        onClick={() => navigate('/')}
                    >
                        Home
                    </button>
                </div>
            <h2>About INFERable</h2>
            <p>INFERable is a public benefit corporation with a mission to make a material positive impact on systems 
                of education, training, and human development. We offer modularized AI as a service that can predict 
                and guide optimization of learning and performance and a Skills Architecture based on international 
                standards. We're giving creators and users of online education/training content 
                a way to "plug in" to powerful artificial intelligence and learning analytics. For more information see: 
                <a href="https://inferable.app">inferable.app</a> </p>
        </div>
        </div>
    );
}

function UseModels() {
    const navigate = useNavigate();
    return (
        <div class="container">
            <div id="use-models-page" class="mt-6 text-gray-700 hidden">
            <div className="d-flex justify-content-between align-items-center mb-3">
                    <button
                        id="home-button"
                        className="btn btn-secondary"
                        onClick={() => navigate('/')}
                    >
                        Home
                    </button>
                </div>
                <h2>Use Models</h2>
                <p>Some models published on LearningFace.ai are available as a service to be plugged-into any learning 
                    content or platform. Contact info@inferable.app for more information.</p>
            </div>
        </div>
    );
}

function JoinTeam() {
    const navigate = useNavigate();
    return (
        <div class="container">
        <div id="join-page" className="mt-6 text-gray-700 hidden">
        <div className="d-flex justify-content-between align-items-center mb-3">
                    <button
                        id="home-button"
                        className="btn btn-secondary"
                        onClick={() => navigate('/')}
                    >
                        Home
                    </button>
                </div>
            <h2>Join the Team</h2>
            <p>The Learning Face team is on the lookout for volunteer contributors and team members. Contact info@inferable.app</p>
        </div>  
        </div>
    );
}

function CrudComponent() {
    const [authData, setAuthData] = useState(null); // State to manage auth data
    const [loading, setLoading] = useState(true); // State to manage loading status

    // Function to fetch authentication status
    const fetchAuthStatus = async () => {
        try {
            const data = await getAuthData(); // Fetch auth data
            setAuthData(data); // Update auth data state
        } catch (error) {
            console.error('Error fetching auth status:', error);
        } finally {
            setLoading(false); // Set loading to false after fetching
        }
    };

    // Fetch authentication status on component mount
    useEffect(() => {
        fetchAuthStatus();
    }, []);

    // Show a loading message while fetching auth data
    if (loading) {
        return (
            <div className="container mt-5">
                <h1>Loading...</h1>
                <p>Checking authentication status...</p>
            </div>
        );
    }

    // Show a message if the user is not authenticated
    if (!authData || !authData.isAuthenticated) {
        return (
            <div className="container mt-5">
                <h1>LearningFace.ai Contributor</h1>
                <div id="auth-section">
                    <p>You need to authenticate to use this application.</p>
                </div>
            </div>
        );
    }

    // Render the main content if the user is authenticated
    return <CrudUI />;
}

function UserDetails() {
    const navigate = useNavigate();
    useEffect(() => {
        const authStatus = document.getElementById('authStatus');
        const loginButton = document.getElementById('loginButton');
        const logoutButton = document.getElementById('logoutButton');
        const userDetailsDiv = document.getElementById('userDetails');
        const userNameDisplay = document.getElementById('userName');
        const userEmailDisplay = document.getElementById('userEmail');
        const userImageDisplay = document.getElementById('userImage');
        checkAuthStatus().then((data) => {
            console.log('Auth status data:', data);
            if (data.isAuthenticated) {
                authStatus.textContent = 'Authenticated';
                loginButton.style.display = 'none';
                logoutButton.style.display = 'block';
                userNameDisplay.textContent = data.user.name;
                userEmailDisplay.textContent = data.user.email;
                userImageDisplay.src = data.user.picture;
                userImageDisplay.style.display = 'block';
            } else {
                console.error('Error fetching auth status:', error);
                authStatus.textContent = 'Not Authenticated';
                loginButton.style.display = 'block';
                logoutButton.style.display = 'none';
                userNameDisplay.textContent = '<not authenticated>';
                userEmailDisplay.textContent = '<not authenticated>';
                userImageDisplay.style.display = 'none';  
            }
        }).catch((error) => {
            console.error('Error fetching auth status:', error);
            authStatus.textContent = 'Not Authenticated';
            loginButton.style.display = 'block';
            logoutButton.style.display = 'none';
            userNameDisplay.textContent = '<not authenticated>';
            userEmailDisplay.textContent = '<not authenticated>';
            userImageDisplay.style.display = 'none';
        });
    }, []);
    
    return (
        <div class="container">
            <div id="userDetails" class="mt-6 text-gray-700 hidden">
                    <button
                        id="home-button"
                        className="btn btn-secondary"
                        onClick={() => navigate('/')}
                    >
                        Home
                    </button>
                <h2 class="text-lg font-semibold mb-2">User Details:</h2>
                <p><span class="font-medium">Name:</span> <span id="userName"></span></p>
                <p><span class="font-medium">Email:</span> <span id="userEmail"></span></p>
                <img id="userImage"  src="INFERableLearningFaceLogo.png" alt="User Profile Image" ></img>
            </div>
        </div>
    )
}

function SiteTerms() {
    const navigate = useNavigate();
    return (
        <div class="container">
        <div id="join-page" className="mt-6 text-gray-700 hidden">
        <div className="d-flex justify-content-between align-items-center mb-3">
                    <button
                        id="home-button"
                        className="btn btn-secondary"
                        onClick={() => navigate('/')}
                    >
                        Home
                    </button>
                </div>
                <h5 style={{marginTop: 0}}>Notice & Terms of Use</h5>
                <p style={{fontSize: '1rem'}}>
                    This site is provided by INFERable, a Public Benefit Corporation, and its affiliates, on an "<b>as is</b>" basis and <b>without warranties of any kind, either express or implied</b>.<br />
                    <br /><b>Your use of this site signifies your agreement to these terms.</b><br />
<br/>By continuing to use this site, you acknowledge and accept that:<br />
&bull; <b>Content and Information Collection:</b> Your activity and certain personal information, such as email addresses for authenticated users and contributors, may be collected. Please review our [Link to Privacy Policy] for more details on how we collect, use, and protect your information.<br />
&bull; <b>User Responsibility for Content:</b> You are solely responsible for any content you submit to this site. You agree that your content will not violate any applicable laws, including but not limited to copyright, trademark, privacy, or defamation laws. We reserve the right, but have no obligation, to monitor, edit, or remove any content at our sole discretion.<br />
&bull; <b>Indemnification:</b> You agree to indemnify, defend, and hold harmless INFERable, its affiliates, officers, directors, employees, and agents from and against any and all claims, liabilities, damages, losses, costs, and expenses (including reasonable attorneys' fees) arising out of or in any way connected with your access to or use of the site, your submission of content, or your violation of these terms.<br />
&bull; <b>Cookies:</b> Cookies and similar technologies may be used to support required site functions and enhance your user experience. For more information, please see our [Link to Cookie Policy].<br />
&bull; <b>No Legal Advice:</b> The content on this site, including user-contributed content, is for informational purposes only and does not constitute legal, financial, medical, or professional advice. You should consult with a qualified professional for advice tailored to your specific situation.<br />
 By proceeding, you confirm that you have read, understood, and agree to be bound by these terms. If you do not agree to these terms, please discontinue your use of the site.<br />
                </p>
            </div>  
        </div>
    );
}

function RootHandler() {
        // Check if the user just authenticated from a page and redirect accordingly
        const navigate = useNavigate();
        // After successful authentication:
        const redirectPath = localStorage.getItem('postLoginRedirect');
        if (redirectPath) {
            localStorage.removeItem('postLoginRedirect');
            console.log('Redirecting to:', redirectPath);
            window.location.replace(redirectPath);
        } else {
            return <HomePage />;
        }
}

function App() {
    useEffect(() => {
        // Some function here to initialize the app, if needed
    }, []);

    return (
        <BrowserRouter>
            {/* MenuWidget and legal overlay is placed outside of Routes to ensure it is always displayed */}
            <MenuWidget />
            <LegalOverlay />
            <Routes>
                <Route path="/" element={<RootHandler />} />
                <Route path="/crud" element={<CrudComponent />} />
                <Route path="/about-lf" element={<AboutLearningFace />} />
                <Route path="/about-inf" element={<AboutInferable />} />
                <Route path="/model-details/:id" element={<ModelDetails />} />
                <Route path="/use" element={<UseModels />} />
                <Route path="/join" element={<JoinTeam />} />
                <Route path="/user-details" element={<UserDetails />} />
                <Route path="/site-terms" element={<SiteTerms />} />
                {/* Add more routes as needed */}
            </Routes>
        </BrowserRouter>
    );
}

//  Render your main App component
const root = ReactDOM.createRoot(document.getElementById('root')); //create root
root.render(<App />); //use root to render